import { useMemo } from "react";
import {
  validateTextField,
  validateNumberField,
  validateDateField,
  validateSelectField,
  validateBooleanField,
  validateEmailField,
  validatePhoneField,
} from "../services/validation";

interface FieldConfig {
  type: "text" | "number" | "date" | "boolean" | "select" | "email" | "phone";
  required?: boolean;
  minValue?: number;
  maxLength?: number;
  customValidation?: (value: any) => string;
}

interface FormChangesConfig<T> {
  fields: Record<string, FieldConfig>;
  initialData?: T | null;
  currentData: T;
  isEditMode: boolean;
}

export const useFormChanges = <T extends Record<string, any>>({
  fields,
  initialData,
  currentData,
  isEditMode,
}: FormChangesConfig<T>) => {
  const hasChanges = useMemo(() => {
    // For new items (not edit mode), check if required fields have valid values
    if (!isEditMode) {
      return Object.entries(fields).every(([fieldName, config]) => {
        const value = currentData[fieldName as keyof T];

        if (config.required) {
          switch (config.type) {
            case "text":
            case "date":
              return typeof value === "string" && value.trim() !== "";
            case "number":
              return typeof value === "number" && value > 0;
            case "boolean":
              return typeof value === "boolean";
            case "select":
              return value !== "" && value !== null && value !== undefined;
            default:
              return true;
          }
        }

        return true;
      });
    }

    // For edit mode, compare current data with initial data
    if (!initialData) return false;

    return JSON.stringify(currentData) !== JSON.stringify(initialData);
  }, [fields, initialData, currentData, isEditMode]);

  const validateField = (fieldName: string, value: any): string => {
    const config = fields[fieldName];
    if (!config) return "";

    let error = "";

    // Use validation service functions
    switch (config.type) {
      case "text":
        error = validateTextField(value, config.required, config.maxLength);
        break;
      case "number":
        error = validateNumberField(value, config.required, config.minValue);
        break;
      case "date":
        error = validateDateField(value, config.required);
        break;
      case "select":
        error = validateSelectField(value, config.required);
        break;
      case "boolean":
        error = validateBooleanField(value, config.required);
        break;
      case "email":
        error = validateEmailField(value, config.required);
        break;
      case "phone":
        error = validatePhoneField(value, config.required);
        break;
    }

    // Custom validation
    if (!error && config.customValidation) {
      error = config.customValidation(value);
    }

    return error;
  };

  const validateAllFields = (): Record<string, string> => {
    const errors: Record<string, string> = {};

    Object.keys(fields).forEach((fieldName) => {
      const value = currentData[fieldName as keyof T];
      const error = validateField(fieldName, value);
      if (error) {
        errors[fieldName] = error;
      }
    });

    return errors;
  };

  const hasValidationErrors = (errors: Record<string, string>): boolean => {
    return Object.values(errors).some((error) => error !== "");
  };

  return {
    hasChanges,
    validateField,
    validateAllFields,
    hasValidationErrors,
  };
};
